import React, { useMemo, useState, useCallback } from 'react'
import { useGetTreeById } from 'shared/hooks/trees/hooks'
import { useGetGradeForTree } from 'shared/api/grades'
import { Box, Button } from '@mui/material'
import { useSetAtom } from 'infrastructure/state/jotai.wrapper'
import { lessonMappingTreesAtom } from 'domains/scope-and-sequence/features/lesson-mappings/atoms'
import { TreeIdInput } from 'components/tree/navigationHelpers/TreeNodeRouter'
import { Table } from 'shared/ui/Table/Table'
import { Column } from 'shared/ui/Table/types'
import { pluralize } from 'shared/utils'
import { CrosswalkMappingSetType } from './types'

type LabeledStatusProps = {
  label: string
  isComplete: boolean
  completeText?: string
  incompleteText?: string
}

export function LabeledStatus({
  label,
  isComplete,
  completeText,
  incompleteText,
}: LabeledStatusProps) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        padding: '8px 0',
      }}
    >
      <span style={{ fontWeight: 500 }}>{label}</span>

      <StatusIndicator
        isComplete={isComplete}
        completeText={completeText}
        incompleteText={incompleteText}
      />
    </div>
  )
}

type StatusIndicatorProps = {
  isComplete: boolean
  completeText?: string
  incompleteText?: string
  className?: string // allow custom styling
}

export function StatusIndicator({
  isComplete,
  completeText = 'All Set',
  incompleteText = 'More Needed',
  className,
}: StatusIndicatorProps) {
  return (
    <div
      className={className}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 12px',
        borderRadius: '6px',
        backgroundColor: isComplete ? '#e8f5e9' : '#ffebee',
        color: isComplete ? '#2e7d32' : '#c62828',
        fontWeight: 500,
      }}
    >
      <span style={{ fontSize: '18px' }}>{isComplete ? '✔️' : '⚠️'}</span>
      <span>{isComplete ? completeText : incompleteText}</span>
    </div>
  )
}

export function TreeSelectWithList({
  currentTrees,
  setCurrentTrees,
}: {
  currentTrees: { treeId: string; versionId: string; title: string }[]
  setCurrentTrees: React.Dispatch<
    React.SetStateAction<{ treeId: string; versionId: string; title: string }[]>
  >
}) {
  const [adding, setAdding] = useState(false)

  const onSelect = (params: {
    treeId: string
    versionId: string
    title: string
  }) => {
    setCurrentTrees((prev) => [...prev, params])
    setAdding(false)
  }
  const canAdd = (ids: { treeId: string; versionId: string }) =>
    !currentTrees.some(({ treeId }) => treeId === ids.treeId)

  const cols: Column<{ treeId: string; versionId: string; title: string }>[] = [
    {
      title: 'Tree',
      rowContent: ({ title }) => title,
    },
    {
      title: 'Grade',
      rowContent: ({ treeId, versionId }) => (
        <GradeColumn treeId={treeId} versionId={versionId} />
      ),
    },
    {
      title: 'Remove',
      rowContent: ({ treeId }) => <RemoveTreeButton treeId={treeId} />,
    },
  ]

  return (
    <Box>
      {adding && (
        <Box sx={{ pb: 2 }}>
          <Box sx={{ fontWeight: 500, pb: 1 }}>
            Add Tree{' '}
            <Button size="small" onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </Box>
          <TreeIdInput
            onChange={onSelect}
            cta={'Add Tree'}
            canSelectTree={(ids) => canAdd(ids)}
          />
        </Box>
      )}
      <Box>
        <Table
          title={`${currentTrees.length} ${pluralize('Tree', currentTrees.length)} Included in Lesson Mappings`}
          toolBarItems={[
            <Button key="addTree" onClick={() => setAdding(true)}>
              Add Another
            </Button>,
          ]}
          columns={cols}
          items={currentTrees}
          overrideHeight={210}
        />
      </Box>
    </Box>
  )
}

function GradeColumn(ids: { treeId: string; versionId: string }) {
  const { tree } = useGetTreeById(ids)
  const getGrade = useGetGradeForTree()
  if (!tree) return null
  const grade = getGrade(tree)
  if (!grade) return null
  return <Box>{grade.name}</Box>
}

function RemoveTreeButton({ treeId }: { treeId: string }) {
  const setTrees = useSetAtom(lessonMappingTreesAtom)
  return (
    <Button
      onClick={() =>
        setTrees((prev) => prev.filter((p) => p.treeId !== treeId))
      }
    >
      Remove
    </Button>
  )
}

export function ValidPercentage(props: {
  alignments: CrosswalkMappingSetType['alignments']
  param: 'source_valid' | 'target_valid'
}) {
  const calculatePercentageValid = useCallback(
    (
      alignments: CrosswalkMappingSetType['alignments'],
      key: 'source_valid' | 'target_valid' = 'source_valid',
    ): number => {
      if (!alignments || !alignments.length) {
        return 0
      }

      const total = alignments.length
      const totalValid = alignments.reduce((acc, item) => {
        return acc + (item[key] ? 1 : 0)
      }, 0)

      return (totalValid / total) * 100
    },
    [],
  )

  const getColor = useCallback((value: number) => {
    if (value < 70) {
      return '#d32f2f'
    } else if (value < 90) {
      return '#A18632'
    } else {
      return '#007B4B'
    }
  }, [])

  const percentage = useMemo(
    () => calculatePercentageValid(props.alignments, props.param),
    [calculatePercentageValid, props.alignments, props.param],
  )

  return (
    <span style={{ color: getColor(percentage) }}>
      {percentage.toFixed(2)}% Valid
    </span>
  )
}
